<template>
  <div class="relative flex items-start">
    <div class="flex h-6 items-center">
      <input
        :id="customSizeRadioButtonId"
        v-model="model"
        type="radio"
        value="custom"
        class="h-4 w-4 border-black text-transparent checked:border-2 checked:border-black hover:border-2 hover:border-black checked:hover:border-2 checked:hover:border-black focus:border-2 focus:border-black focus:ring-transparent checked:focus:border-2 checked:focus:border-black active:border-2 active:border-black"
      />
    </div>
    <label
      :for="customSizeRadioButtonId"
      class="ml-3 flex w-full cursor-pointer items-center justify-between leading-6"
    >
      <strong
        v-t="'pages.configurator.preferred_sizes.choose_format'"
        class="font-bold"
      />

      <div
        v-if="model === 'custom'"
        class="flex cursor-pointer items-center justify-between leading-6"
      >
        <div class="flex items-center justify-end gap-3">
          <span
            class="text-gray-lighter line-through"
            v-html="sizeStore.selectedSizeOption?.localizedRecommendedPrice"
          />
          <span
            class="font-semibold"
            v-html="sizeStore.selectedSizeOption?.localizedPrice"
          />
        </div>
      </div>
    </label>
  </div>

  <div v-if="model === 'custom'" class="mt-4 flex w-full gap-4">
    <ConfiguratorDropdown
      v-if="Object.keys(sizeStore.sizes ?? {}).length"
      v-model="sizeStore.selectedSizeOption"
      :options="sizeStore.sizeOptions"
      class="w-full"
      show-above
    />
  </div>
</template>

<script setup lang="ts">
import { useSizeStore } from '@/stores/Configurator/SizeStore';
import type { SelectorOption } from '@/types/Configurator/SelectorOption';
import _ from 'lodash';
import { nextTick, onMounted, ref, watch } from 'vue';
import ConfiguratorDropdown from './ConfiguratorDropdown.vue';

const sizeStore = useSizeStore();
const model = ref<string | null>(null);

defineProps({
  customSizeRadioButtonId: {
    type: String,
    required: false,
    default: 'custom-size-radio-button-desktop',
  },
});

watch(
  () => model.value,
  newValue => {
    if (newValue !== 'custom') {
      return;
    }

    // Set selected option
    if (
      !_.find(
        sizeStore.sizeOptions,
        (option: SelectorOption) =>
          option.value === sizeStore.selectedSizeOption.value
      )
    ) {
      sizeStore.selectedSizeOption = sizeStore.sizeOptions[0];
    }

    // Set previous selected option
    sizeStore.previousSelectedSizeOption = sizeStore.selectedSizeOption;
  }
);

watch(
  () => sizeStore.selectedSizeValue,
  newValue => {
    // If new selected size is a preferred size, unset radio button
    if (
      _.find(
        sizeStore.sizeOptions,
        option => option.value === newValue.attributes.sku
      )
    ) {
      model.value = 'custom';
      // Handle scroll to bottom
      handleScroll();
      return;
    }

    model.value = null;
  }
);

function handleScroll() {
  // Get elements
  const configuratorContainerDesktop = document.getElementById(
    'configuratorContainerDesktop'
  );
  const configuratorContainerMobile = document.getElementById(
    'configuratorContainerMobile'
  );

  // Scroll to bottom of containers
  scrollToBottomOfContainer(configuratorContainerDesktop);
  scrollToBottomOfContainer(configuratorContainerMobile);
}

async function scrollToBottomOfContainer(container: HTMLElement | null) {
  if (container) {
    await nextTick();
    container.scrollTo({
      top: container.scrollHeight,
      behavior: 'smooth',
    });
  }
}

onMounted(() => {
  // On load, check if selected size is a custom size
  if (
    _.find(
      sizeStore.sizeOptions,
      (size: SelectorOption) =>
        size.value === sizeStore.selectedSizeValue.attributes.sku
    )
  ) {
    model.value = 'custom';
    return;
  }
  model.value = null;
});
</script>
